import type { Directive, DirectiveBinding } from "vue";

import { MatomoHelper } from "@/helpers";
import type { MatomoEventAction, MatomoEventCategory } from "@/types";

export interface EventParams {
  action: MatomoEventAction;
  category: MatomoEventCategory;
  dimensions?: unknown;
  label?: string;
  eventValue?: unknown;
}

const eventParamsMap = new WeakMap<HTMLElement, EventParams>();

/**
 * Set event params for element
 */
function setEventParams(el: HTMLElement, { arg: eventType = "click", value: eventParams }: DirectiveBinding) {
  if (eventParams.label) {
    // Due to the Vue compiler, we use single quotes in the directive,
    // but since all events use double quotes, we replace them here.
    eventParams.label = eventParams.label.replaceAll("'", '"');
  }

  eventParamsMap.set(el, {
    action: eventType,
    ...eventParams
  });
}

/**
 * Return event params for element
 */
function getEventParams(el: HTMLElement): EventParams | undefined {
  return eventParamsMap.get(el);
}

const listenersMap = new WeakMap<HTMLElement, () => void>();

/**
 * Add event listener to element, while keeping a
 * reference to a cleanup function for later removal.
 */
function addEventListener(el: HTMLElement, eventType: string, listener: () => void, once = false) {
  el.addEventListener(eventType, listener, {
    passive: true,
    once
  });

  listenersMap.set(el, () => {
    el.removeEventListener(eventType, listener);
  });
}

/**
 * Remove event listener from element by using
 * reference to previously stored cleanup function.
 */
function removeEventListener(el: HTMLElement) {
  listenersMap.get(el)?.();
}

const observersMap = new WeakMap<HTMLElement, () => void>();

/**
 * Destroy observer
 */
function destroyObserver(el: HTMLElement) {
  observersMap.get(el)?.();
}

/**
 * Send event to Matomo using a directive.
 *
 * Example:
 *     <button v-matomo-event:EVENT_TYPE.MODIFIERS="EVENT_PARAMS">
 *         Button
 *     </button>
 *
 * EVENT_TYPE {"click"|"mouseover"|"focus"} Accepts any DOM element event type. Defaults to `click`
 *
 * MODIFIER {once} if `once` is used, only one event is sent
 *
 * EVENT_PARAMS {Object} Params to send to Matomo
 * {
 *   `action` {string} defaults to EVENT_TYPE
 *   `category` {string}
 *   `label` {string}
 *   `eventValue` {number}
 * }
 */
export const vMatomoEvent: Directive<HTMLElement, EventParams> = {
  beforeMount(el, binding) {
    const { arg: eventType = "click", modifiers } = binding;

    setEventParams(el, binding);

    addEventListener(
      el,
      eventType,
      () => {
        const eventParams = getEventParams(el);
        if (eventParams) {
          const { category, action, label } = eventParams;
          MatomoHelper.sendEvent(category, action, label);
        }
      },
      modifiers.once
    );
  },

  /**
   * Update event params when it changes in order to support dynamic values
   */
  updated(el, binding) {
    setEventParams(el, binding);
  },

  unmounted(el) {
    // If `eventType` causes element removal from DOM, the directive's `unbind` method
    // is called, which will remove event listeners before they get a chance to run.
    // To prevent this we delay cleanup by an arbitrary amount of time.
    setTimeout(() => {
      removeEventListener(el);
      destroyObserver(el);
    }, 10);
  }
};
